import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import Colours from '@shared/ui/atoms/Colours';
import { Trans, useTranslation } from 'react-i18next';
import { AccountType } from '@shared/models/company.models';
import { loadAccountInfo, selectAccountInfo } from '@features/settings/store/payments.duck';
import { closeDialog } from '@shared/store/dialog.duck';
import {
  changeSubscriptionPlan,
  changeSubscriptionQuantity,
  getCustomerPortal
} from '@shared/ui/organisms/stripe/stripeService';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { TrackJS } from 'trackjs';
import { ManageSeatsSection } from './ManageSeatsSection';

export function ManageSubscriptionDialog() {
  const dispatch = useDispatch();
  const accountInfo = useSelector(selectAccountInfo);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showDowngradeMessage, setShowDowngradeMessage] = useState(false);

  const currentTier = accountInfo?.accountType;
  const currentSpaces = accountInfo?.paidUsers ?? 0;

  // Plan tier and paid spaces are staged locally; nothing is persisted until "Update licenses".
  const [selectedTier, setSelectedTier] = useState<AccountType | undefined>(currentTier);
  const [spaces, setSpaces] = useState<number>(currentSpaces);

  useEffect(() => {
    setSelectedTier(accountInfo?.accountType);
  }, [accountInfo?.accountType]);

  useEffect(() => {
    if (accountInfo?.paidUsers != null) {
      setSpaces(accountInfo.paidUsers);
    }
  }, [accountInfo?.paidUsers]);

  const close = () => dispatch(closeDialog());

  // Downgrades (Premium → Professional/Free, Professional → Free) aren't self-service.
  const isDowngrade = (tier: AccountType) =>
    (currentTier === AccountType.ENTERPRISE && tier !== AccountType.ENTERPRISE) ||
    (currentTier === AccountType.PREMIUM && tier === AccountType.STANDARD) ||
    (currentTier === AccountType.PREMIUM && tier === AccountType.PROFESSIONAL) ||
    (currentTier === AccountType.PROFESSIONAL && tier === AccountType.STANDARD);

  const selectTier = (tier: AccountType) => {
    if (isDowngrade(tier)) {
      setShowDowngradeMessage(true);
      return;
    }
    setShowDowngradeMessage(false);
    setSelectedTier(tier);
  };

  const tierChanged = !!selectedTier && selectedTier !== currentTier;
  const spacesChanged = spaces !== currentSpaces;
  const hasChanges = tierChanged || spacesChanged;

  const updateLicenses = async () => {
    if (!hasChanges) return;
    setIsLoading(true);
    try {
      if (tierChanged && selectedTier) {
        await changeSubscriptionPlan(selectedTier);
      }
      if (spacesChanged) {
        // Send the requested paid USER count as-is; the backend rounds it up to the next whole
        // pack and converts to Stripe's pack quantity itself.
        await changeSubscriptionQuantity(spaces);
      }
      dispatch(loadAccountInfo());
      dispatch(successNotification(t('payments.licenses-updated')));
      dispatch(closeDialog());
    } catch (e: any) {
      console.error(e);
      TrackJS?.track(e);
      dispatch(failureNotification(t('payments.licenses-update-failed')));
    } finally {
      setIsLoading(false);
    }
  };

  const openStripePortal = async () => {
    try {
      setIsLoading(true);
      window.location.href = (await getCustomerPortal()).url;
    } catch (e: any) {
      console.error(e);
      TrackJS?.track(e);
      dispatch(failureNotification('There has been an issue opening the Stripe payments portal'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledDialog isOpen={true} onClose={close} showLogo={true}>
      <ContentContainer>

        {isLoading && <LoadingSpinner fullScreen={true} />}
        <DialogHeader title={'payments.manage-subscription-title'} />

        <ChangePlanContainer>
          <BodySmall weight={600} style={{ margin: '0px auto 8px auto', textAlign: 'center' }}>{t('payments.your-plan')}</BodySmall>
          <TierToggleRow>
            {currentTier === AccountType.STANDARD &&
              <ToggleableOutlineButton isActive={selectedTier === AccountType.STANDARD}
                                       click={() => selectTier(AccountType.STANDARD)}
                                       text="payments.free-plan" />}
              <ToggleableOutlineButton isActive={selectedTier === AccountType.PROFESSIONAL}
                                       click={() => selectTier(AccountType.PROFESSIONAL)}
                                       text="payments.professional-plan" />
              <ToggleableOutlineButton isActive={selectedTier === AccountType.PREMIUM}
                                       click={() => selectTier(AccountType.PREMIUM)}
                                       text="payments.premium-plan" />
          </TierToggleRow>
          {showDowngradeMessage &&
            <BodyRegular weight={400} style={{ marginTop: 8 }}>
              <Trans i18nKey={'payments.downgrade-contact-support'}
                     components={[<a style={{ color: Colours.blue }} href={'mailto:support@team-today.com'}>support@team-today.com</a>]} />
            </BodyRegular>}
        </ChangePlanContainer>

        <ManageSeatsSection value={spaces} onChange={setSpaces} />

        <ActionButtons>
          <PrimaryButton size={ButtonSize.Medium}
                         click={updateLicenses}
                         text="payments.update-licenses"
                         disabled={isLoading || !hasChanges}
                         style={{ width: 260 }} />
        </ActionButtons>
        {accountInfo?.usesStripe &&
          <InfoRow>
            <div></div>
            <img src="/assets/icons/powered-by-stripe-purple.svg" alt="Powered by Stripe" />
          </InfoRow>}
      </ContentContainer>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)<any>`
  .dialog__content {
    max-width: 680px;
  }
`;

const ContentContainer = styled.div`
  max-width: 100%;
  width: 600px;
`

const ChangePlanContainer = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${Colours.mildGrey};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TierToggleRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const InfoRow = styled(SpaceBetweenRow)`
  padding: 12px 0;
`;
